import { ImgID } from "../../image_loader/img_id.js";
import { LocalizationManager } from "../../localization/localization_manager.js";
import { ColorScheme } from "../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../common_ui_elements/text_style_scheme.js";
import { UIImage } from "../common_ui_elements/ui_image.js";
import { UINineSliceElement } from "../common_ui_elements/ui_nine_slice_element.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../common_ui_elements/ui_text_element.js";
import { AbstractTooltip } from "./abstract_tooltip.js";

export class EventLockedTooltip extends AbstractTooltip
{
  private static readonly DESIGN_TAPE_W = 675;
  private static readonly DESIGN_TAPE_H = 200;
  private static readonly DESIGN_TAIL_W = 110.46;
  private static readonly DESIGN_TAIL_H = 77.12;
  private static readonly DESIGN_TAIL_Y = 167.68;
  private static readonly DESIGN_SIDE_MARGIN = 33;
  private static readonly DESIGN_ABOVE_ANCHOR = 219.75;

  private readonly on_close: () => void;

  public static create(
    ctx: CanvasRenderingContext2D,
    anchor_center_x: number,
    anchor_top: number,
    screen_w: number,
    unlock_level: number,
    on_close: () => void
  ): EventLockedTooltip
  {
    const tape_w = LayoutScaler.widthFromDesign( EventLockedTooltip.DESIGN_TAPE_W );
    const tape_h = LayoutScaler.heightFromDesign( EventLockedTooltip.DESIGN_TAPE_H );
    const tail_w = LayoutScaler.widthFromDesign( EventLockedTooltip.DESIGN_TAIL_W );
    const tail_h = LayoutScaler.heightFromDesign( EventLockedTooltip.DESIGN_TAIL_H );
    const tail_y = LayoutScaler.heightFromDesign( EventLockedTooltip.DESIGN_TAIL_Y );
    const side_margin = LayoutScaler.widthFromDesign( EventLockedTooltip.DESIGN_SIDE_MARGIN );

    const x = Math.min( Math.max( anchor_center_x - tape_w / 2, side_margin ), screen_w - side_margin - tape_w );
    const y = anchor_top - LayoutScaler.heightFromDesign( EventLockedTooltip.DESIGN_ABOVE_ANCHOR );
    const h = Math.max( tape_h, tail_y + tail_h );
    const tail_x = Math.min( Math.max( anchor_center_x - x - tail_w / 2, 0 ), tape_w - tail_w );

    return new EventLockedTooltip( ctx, x, y, tape_w, h, tail_x, unlock_level, on_close );
  }

  private constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    w: number,
    h: number,
    tail_x: number,
    unlock_level: number,
    on_close: () => void
  )
  {
    super( ctx, x, y, w, h );

    this.on_close = on_close;

    const tape_h = LayoutScaler.heightFromDesign( EventLockedTooltip.DESIGN_TAPE_H );
    const tape = new UINineSliceElement( ctx, 0, 0, w, tape_h, ImgID.UI_GameBoosterLockedTape, "booster_tape" );
    this.addChild( tape, true );

    const text_x = LayoutScaler.xFromDesign( 30 );
    const text_y = LayoutScaler.yFromDesign( 20 );
    const text_w = w - LayoutScaler.widthFromDesign( 60 );
    const text_h = tape_h - LayoutScaler.heightFromDesign( 40 );

    const text = new UITextElement( ctx, text_x, text_y, text_w, text_h, {
      text: LocalizationManager.tWithParams( "MENU.EVENT_UNLOCKS_AT", [ String( unlock_level ) ] ),
      font_size: LayoutScaler.textSizeFromDesign( 52 ),
      font_weight: 900,
      text_color: ColorScheme.tooltip_text_outline,
      text_style: TextStyleScheme.rubik,
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
      outline_color: ColorScheme.white,
      fit_font_size: true,
      min_font_size: LayoutScaler.textSizeFromDesign( 30 ),
      line_height: 0.95,
      auto_wrap: true,
      shadow_offset_x: 0,
      shadow_offset_y: 0
    } );
    tape.addChild( text, true );

    const tail_w = LayoutScaler.widthFromDesign( EventLockedTooltip.DESIGN_TAIL_W );
    const tail_h = LayoutScaler.heightFromDesign( EventLockedTooltip.DESIGN_TAIL_H );
    const tail_y = LayoutScaler.heightFromDesign( EventLockedTooltip.DESIGN_TAIL_Y );

    const tail = new UIImage( ctx, tail_x, tail_y, tail_w, tail_h, ImgID.UI_GameBoosterBottom );
    this.addChild( tail, true );
  }

  public cleanup(): void
  {
    this.on_close();
  }

  public name(): string
  {
    return "EventLockedTooltip";
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    this.startRender();
    this.finishRender();
  }
}
